import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApp, User } from './AppContext';
import { doc, onSnapshot, getFirestore } from 'firebase/firestore';
import { getApp } from 'firebase/app';

const STORAGE_KEY = 'rovita_active_call_session';
const CHANNEL_NAME = 'rovita_call_sync_channel';

export interface ActiveCallSession {
  appointmentId: string;
  tabId: string;
  isDoctor: boolean;
  connected: boolean;
  timestamp: number;
}

export interface ParticipantOnlineStatus {
  isOnline: boolean;
  isInRoom: boolean;
  label: string;
  sublabel?: string;
  badgeColor: 'emerald' | 'amber' | 'slate';
}

export interface CallSessionDocData {
  bothConnected?: boolean;
  doctorOnline?: boolean;
  patientOnline?: boolean;
  callEnded?: boolean;
  inProgress?: boolean;
  status?: string;
  endedAt?: number;
  endedBy?: string;
  endedByName?: string;
  doctorJoinedAt?: number;
  patientJoinedAt?: number;
  currentSessionId?: string;
}

export const getParticipantOnlineStatus = (
  otherUser?: Partial<User> | null,
  callStatus?: CallSessionDocData | null,
  isDoctorViewing: boolean = false
): ParticipantOnlineStatus => {
  const isInRoom = isDoctorViewing ? Boolean(callStatus?.patientOnline) : Boolean(callStatus?.doctorOnline);
  if (isInRoom && !callStatus?.callEnded) {
    return {
      isOnline: true,
      isInRoom: true,
      label: isDoctorViewing ? 'Patient is in room (Ready)' : 'Doctor is in room (Waiting for you)',
      badgeColor: 'emerald'
    };
  }

  if (otherUser) {
    const lastSeenTime = typeof otherUser.lastSeen === 'number'
      ? otherUser.lastSeen
      : (otherUser.lastSeen ? new Date(otherUser.lastSeen).getTime() : 0);
    const now = Date.now();
    const timeDiff = now - lastSeenTime;

    // Online if explicitly flagged online or active within the last 90 seconds
    const isRecentlyActive = otherUser.isOnline === true || (lastSeenTime > 0 && timeDiff < 90000);

    if (isRecentlyActive) {
      return {
        isOnline: true,
        isInRoom: false,
        label: isDoctorViewing ? 'Patient Online' : 'Doctor Online',
        sublabel: 'Active on ROVITA',
        badgeColor: 'emerald'
      };
    }

    // Active in last 10 minutes
    if (timeDiff < 10 * 60000 && lastSeenTime > 0) {
      const minsAgo = Math.max(1, Math.floor(timeDiff / 60000));
      return {
        isOnline: true,
        isInRoom: false,
        label: isDoctorViewing ? `Patient active ${minsAgo}m ago` : `Doctor active ${minsAgo}m ago`,
        sublabel: `${minsAgo}m ago`,
        badgeColor: 'amber'
      };
    }
  }

  return {
    isOnline: false,
    isInRoom: false,
    label: isDoctorViewing ? 'Patient is offline' : 'Doctor is offline',
    sublabel: 'Offline',
    badgeColor: 'slate'
  };
};

export const ParticipantStatusBadge: React.FC<{
  user?: Partial<User> | null;
  callStatus?: { doctorOnline?: boolean; patientOnline?: boolean; bothConnected?: boolean } | null;
  isDoctorViewing?: boolean;
  className?: string;
  compact?: boolean;
}> = ({ user, callStatus, isDoctorViewing = false, className = '', compact = false }) => {
  const status = getParticipantOnlineStatus(user, callStatus, isDoctorViewing);

  if (status.isInRoom) {
    return (
      <span 
        title={status.label}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-black bg-emerald-50 text-emerald-700 border border-emerald-300 shadow-2xs ${className}`}
      >
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        </span>
        <span className="truncate">{compact ? 'In Room' : status.label}</span>
      </span>
    );
  }

  if (status.badgeColor === 'emerald') {
    return (
      <span 
        title={`${status.label} - Ready to connect`}
        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50/80 text-emerald-700 border border-emerald-200/80 shadow-2xs ${className}`}
      >
        <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
        <span className="truncate">{compact ? 'Online' : status.label}</span>
      </span>
    );
  }

  if (status.badgeColor === 'amber') {
    return (
      <span 
        title={status.label}
        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200/80 shadow-2xs ${className}`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0"></span>
        <span className="truncate">{status.label}</span>
      </span>
    );
  }

  return (
    <span 
      title={status.label}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-500 border border-slate-200/70 ${className}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-slate-300 shrink-0"></span>
      <span className="truncate">{compact ? 'Offline' : status.label}</span>
    </span>
  );
};

// Generate or retrieve unique ID for this browser tab
export const getCurrentTabId = (): string => {
  if (typeof window === 'undefined') return 'server';
  let tabId = sessionStorage.getItem('rovita_tab_id');
  if (!tabId) {
    tabId = 'tab_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now().toString(36);
    try {
      sessionStorage.setItem('rovita_tab_id', tabId);
    } catch (e) {}
  }
  return tabId;
};

// Register this tab as the active call host with automatic heartbeat
export const registerActiveCallTab = (
  appointmentId: string,
  isDoctor: boolean,
  connected: boolean = false
): (() => void) => {
  if (typeof window === 'undefined' || !appointmentId) return () => {};

  const currentTabId = getCurrentTabId();
  let channel: BroadcastChannel | null = null;
  try {
    if ('BroadcastChannel' in window) {
      channel = new BroadcastChannel(CHANNEL_NAME);
    }
  } catch (e) {}

  const writeSession = (isConnected: boolean) => {
    const sessionData: ActiveCallSession = {
      appointmentId,
      tabId: currentTabId,
      isDoctor,
      connected: isConnected,
      timestamp: Date.now()
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
    } catch (e) {}

    if (channel) {
      try {
        channel.postMessage({ type: 'CALL_ACTIVE', session: sessionData });
      } catch (e) {}
    }
  };

  // Initial announcement
  writeSession(connected);

  // Heartbeat every 1500ms so other tabs know this tab is actively alive
  const interval = setInterval(() => {
    writeSession(connected);
  }, 1500);

  // Respond to status queries from newly opened tabs
  const onChannelMessage = (event: MessageEvent) => {
    if (event.data?.type === 'QUERY_CALL_STATUS') {
      writeSession(connected);
    }
  };

  if (channel) {
    channel.addEventListener('message', onChannelMessage);
  }

  const cleanUp = () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', cleanUp);
    }
    clearInterval(interval);
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.tabId === currentTabId && parsed.appointmentId === appointmentId) {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (e) {}

    if (channel) {
      try {
        channel.postMessage({ type: 'CALL_ENDED', appointmentId, tabId: currentTabId });
        channel.removeEventListener('message', onChannelMessage);
        channel.close();
      } catch (e) {}
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', cleanUp, { once: true });
  }

  return cleanUp;
};

// Hook for any component (dashboards, banners, modals) to detect active call status
export const useActiveCallStatus = () => {
  const { user, appointments } = useApp();
  const [activeSession, setActiveSession] = useState<ActiveCallSession | null>(null);
  const [callDocStatus, setCallDocStatus] = useState<Record<string, CallSessionDocData>>({});
  const channelRef = useRef<BroadcastChannel | null>(null);

  // 1. Monitor LocalStorage & BroadcastChannel for cross-tab active calls
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkSession = () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed: ActiveCallSession = JSON.parse(raw);
          // Check if session was explicitly marked ended
          const endedTime = localStorage.getItem(`rovita_ended_call_${parsed.appointmentId}`);
          if (endedTime && Date.now() - parseInt(endedTime, 10) < 24 * 60 * 60 * 1000) {
            localStorage.removeItem(STORAGE_KEY);
            setActiveSession(null);
            return;
          }

          // Consider alive if heartbeat is within last 4 seconds
          if (Date.now() - parsed.timestamp < 4000) {
            setActiveSession(parsed);
            return;
          } else {
            // Stale session
            localStorage.removeItem(STORAGE_KEY);
          }
        }
        setActiveSession(null);
      } catch (e) {
        setActiveSession(null);
      }
    };

    checkSession();
    const interval = setInterval(checkSession, 1200);

    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY || e.key?.startsWith('rovita_ended_call_')) {
        checkSession();
      }
    };
    window.addEventListener('storage', onStorage);

    try {
      if ('BroadcastChannel' in window) {
        const bc = new BroadcastChannel(CHANNEL_NAME);
        channelRef.current = bc;
        bc.onmessage = (event) => {
          if (event.data?.type === 'CALL_ACTIVE' && event.data.session) {
            const endedTime = localStorage.getItem(`rovita_ended_call_${event.data.session.appointmentId}`);
            if (!endedTime || Date.now() - parseInt(endedTime, 10) > 24 * 60 * 60 * 1000) {
              setActiveSession(event.data.session);
            }
          } else if (event.data?.type === 'CALL_STARTED') {
            if (event.data.appointmentId) {
              try {
                localStorage.removeItem(`rovita_ended_call_${event.data.appointmentId}`);
              } catch (e) {}
            }
            checkSession();
          } else if (event.data?.type === 'CALL_ENDED') {
            checkSession();
          }
        };
        // Query any active call tab right away
        bc.postMessage({ type: 'QUERY_CALL_STATUS' });
      }
    } catch (e) {}

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', onStorage);
      if (channelRef.current) {
        channelRef.current.close();
      }
    };
  }, []);

  // 2. Real-time Firestore sync on active/upcoming appointments to detect if participants are in call
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const relevantApps = appointments.filter(a => a.status !== 'completed' && a.status !== 'cancelled');
    if (relevantApps.length === 0) return;

    let unsubs: (() => void)[] = [];
    try {
      const db = getFirestore(getApp());
      relevantApps.forEach(app => {
        const callDocRef = doc(db, 'calls', app.id);
        const unsub = onSnapshot(callDocRef, (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            const isEnded = data.callEnded === true || 
              data.status === 'ended' || 
              data.status === 'completed' || 
              (data.inProgress === false && data.doctorOnline === false && data.patientOnline === false);

            setCallDocStatus(prev => ({
              ...prev,
              [app.id]: {
                bothConnected: Boolean(data.bothConnected || (data.doctorOnline === true && data.patientOnline === true)),
                doctorOnline: Boolean(data.doctorOnline),
                patientOnline: Boolean(data.patientOnline),
                callEnded: isEnded,
                inProgress: data.inProgress !== false && !isEnded,
                status: data.status,
                endedAt: data.endedAt,
                endedBy: data.endedBy,
                endedByName: data.endedByName,
                doctorJoinedAt: data.doctorJoinedAt,
                patientJoinedAt: data.patientJoinedAt,
                currentSessionId: data.currentSessionId
              }
            }));
          }
        }, () => {});
        unsubs.push(unsub);
      });
    } catch (e) {}

    return () => {
      unsubs.forEach(u => u());
    };
  }, [appointments]);

  // Check if a call has been concluded/ended by either participant
  const isCallEnded = useCallback((appointmentId?: string): boolean => {
    if (!appointmentId) return false;

    const docStat = callDocStatus[appointmentId];
    const app = appointments.find(a => a.id === appointmentId);

    // If Firestore call document or appointment explicitly indicates the call is live/in-progress,
    // or if a participant is currently in the room, it is definitely NOT ended!
    const isActivelyLive = (docStat && !docStat.callEnded && (docStat.inProgress || docStat.doctorOnline || docStat.patientOnline || docStat.status === 'in-progress')) ||
      (app?.status === 'in-progress' && docStat?.callEnded !== true);

    if (isActivelyLive) {
      // Clean up any stale localStorage ended marker if someone restarted or rejoined
      try {
        if (typeof window !== 'undefined') {
          localStorage.removeItem(`rovita_ended_call_${appointmentId}`);
        }
      } catch (e) {}
      return false;
    }

    try {
      if (typeof window !== 'undefined') {
        const endedTimestamp = localStorage.getItem(`rovita_ended_call_${appointmentId}`);
        if (endedTimestamp) {
          const age = Date.now() - parseInt(endedTimestamp, 10);
          if (age < 24 * 60 * 60 * 1000) return true;
        }
      }
    } catch (e) {}

    if (docStat?.callEnded === true || docStat?.status === 'ended' || docStat?.status === 'completed') {
      return true;
    }
    if (app && app.status !== 'in-progress') {
      return true;
    }
    return false;
  }, [callDocStatus, appointments]);

  // Check if a call is currently active in another tab of this browser
  const isCallActiveInAnotherTab = useCallback((appointmentId?: string): boolean => {
    if (!activeSession) return false;
    const currentTabId = getCurrentTabId();
    const isOtherTab = activeSession.tabId !== currentTabId;
    if (!isOtherTab) return false;
    if (appointmentId) {
      return activeSession.appointmentId === appointmentId;
    }
    return true;
  }, [activeSession]);

  // Check if the current user has already joined the call (in this tab or another tab)
  const hasUserJoinedCall = useCallback((appointmentId?: string): boolean => {
    if (!appointmentId) return false;
    if (activeSession && activeSession.appointmentId === appointmentId) {
      return true;
    }
    const docStat = callDocStatus[appointmentId];
    if (docStat && !docStat.callEnded) {
      if (user?.role === 'doctor' && docStat.doctorOnline) return true;
      if (user?.role === 'patient' && docStat.patientOnline) return true;
    }
    return false;
  }, [activeSession, callDocStatus, user?.role]);

  // Check if both doctor and patient are connected together in the call
  const isBothConnectedInCall = useCallback((appointmentId?: string): boolean => {
    if (appointmentId) {
      const docStat = callDocStatus[appointmentId];
      if (docStat?.bothConnected || (docStat?.doctorOnline && docStat?.patientOnline)) {
        return true;
      }
      return false;
    }
    return Object.values(callDocStatus).some(c => c.bothConnected || (c.doctorOnline && c.patientOnline));
  }, [callDocStatus]);

  // Overall check if a consultation is already actively ongoing for this user
  const isCallOngoing = useCallback((appointmentId?: string): boolean => {
    if (appointmentId && isCallEnded(appointmentId)) {
      return false;
    }
    return isCallActiveInAnotherTab(appointmentId) || isBothConnectedInCall(appointmentId);
  }, [isCallEnded, isCallActiveInAnotherTab, isBothConnectedInCall]);

  // Explicit helper to clear ended state when rejoining / restarting call
  const clearCallEnded = useCallback((appointmentId: string) => {
    if (!appointmentId) return;
    try {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(`rovita_ended_call_${appointmentId}`);
        if (channelRef.current) {
          channelRef.current.postMessage({ type: 'CALL_STARTED', appointmentId });
        }
      }
    } catch (e) {}
  }, []);

  // Explicit helper to close and clean up an ended call across tabs and Firestore
  const markCallEnded = useCallback(async (appointmentId: string) => {
    if (!appointmentId) return;
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(`rovita_ended_call_${appointmentId}`, Date.now().toString());
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            if (parsed.appointmentId === appointmentId) {
              localStorage.removeItem(STORAGE_KEY);
            }
          } catch (e) {}
        }
        if (channelRef.current) {
          try {
            channelRef.current.postMessage({ type: 'CALL_ENDED', appointmentId });
          } catch (e) {}
        }
      }
      const db = getFirestore(getApp());
      const { updateDoc, doc } = await import('firebase/firestore');
      await updateDoc(doc(db, 'calls', appointmentId), {
        callEnded: true,
        status: 'ended',
        inProgress: false,
        doctorOnline: false,
        patientOnline: false,
        bothConnected: false,
        endedAt: Date.now(),
        endedBy: user?.id || 'unknown',
        endedByName: user?.name || (user?.role === 'doctor' ? 'Doctor' : 'Patient')
      }).catch(() => {});
    } catch (e) {}
  }, [user]);

  return {
    activeSession,
    currentTabId: getCurrentTabId(),
    isCallActiveInAnotherTab,
    isBothConnectedInCall,
    hasUserJoinedCall,
    isCallEnded,
    isCallOngoing,
    clearCallEnded,
    markCallEnded,
    callDocStatus
  };
};
